import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  ExternalLink,
  FileText,
  Calendar,
  User,
  Upload,
  Image as ImageIcon,
  Award,
  TrendingUp,
  BarChart3,
  PieChart,
  Eye
} from 'lucide-react';

const QuaReports = ({ role }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReportId, setDeleteReportId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  const backendUrl = 'https://python-backend-68898252993.us-central1.run.app';
  // const backendUrl = 'http://127.0.0.1:5001';

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    image_url: '',
    quarter: '',
    year: new Date().getFullYear()
  });

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/quarterly-reports`);
      setReports(response.data);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to fetch quarterly reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePdfUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
  
    setIsUploading(true);
    setError('');
  
    const file = files[0];
    
    // Enhanced validation with detailed logging
    console.log('File details:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });
    
    if (file.type !== 'application/pdf') {
      const errorMsg = `Invalid file type: ${file.type}. Please upload a PDF file only`;
      console.error(errorMsg);
      setError(errorMsg);
      setIsUploading(false);
      return;
    }
    
    // Increased file size limit to 100MB
    const maxFileSize = 100 * 1024 * 1024; // 100MB instead of 10MB
    if (file.size > maxFileSize) {
      const errorMsg = `File too large: ${(file.size / (1024 * 1024)).toFixed(2)}MB. Must be less than ${maxFileSize / (1024 * 1024)}MB`;
      console.error(errorMsg);
      setError(errorMsg);
      setIsUploading(false);
      return;
    }
    
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
  
    try {
      const token = localStorage.getItem('token');
      console.log('Token exists:', !!token);
      console.log('Backend URL:', backendUrl);
      
      // Log the request details
      console.log('Making upload request to:', `${backendUrl}/api/upload-pdf`);
      
      const response = await fetch(`${backendUrl}/api/upload-pdf`, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData,
      });
      
      // Log response details
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      // Get response text first to handle both JSON and non-JSON responses
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        throw new Error(`Server returned non-JSON response: ${responseText}`);
      }
      
      console.log('Parsed result:', result);
      
      if (response.ok) {
        console.log('Upload successful, URL:', result.url);
        setFormData(prev => ({
          ...prev,
          url: result.url
        }));
        
        // Optional: Show success message
        console.log(`✅ Upload successful! File size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        
      } else {
        const errorMsg = result.error || result.message || `HTTP ${response.status}: ${response.statusText}`;
        console.error('Upload failed:', errorMsg);
        setError(errorMsg);
      }
    } catch (error) {
      console.error("Complete error object:", error);
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
      
      // More specific error messages
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError("Network error: Cannot connect to server. Please check your connection.");
      } else if (error.message.includes('JSON')) {
        setError("Server response error: Invalid response format");
      } else {
        setError(`Upload failed: ${error.message}`);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
  
    setIsUploading(true);
    setError('');
  
    const file = files[0];
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    uploadFormData.append("upload_preset", "Brian_Karsh");
  
    try {
      const response = await fetch("https://api.cloudinary.com/v1_1/dyglrkcfq/image/upload", {
        method: "POST",
        body: uploadFormData,
      });
      const result = await response.json();
      if (response.ok) {
        setFormData(prev => ({
          ...prev,
          image_url: result.secure_url
        }));
      } else {
        console.error("Error uploading to Cloudinary:", result.message);
        setError("Failed to upload image");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setError("Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const openModal = (report = null) => {
    if (report) {
      setEditingReport(report);
      setFormData({
        name: report.name,
        url: report.url,
        image_url: report.image_url || '',
        quarter: report.quarter || '',
        year: report.year || new Date().getFullYear()
      });
    } else {
      setEditingReport(null);
      setFormData({
        name: '',
        url: '',
        image_url: '',
        quarter: '',
        year: new Date().getFullYear()
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingReport(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      let response;

      if (editingReport) {
        response = await axios.put(
          `${backendUrl}/api/quarterly-reports/${editingReport.id}`,
          formData,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
      } else {
        response = await axios.post(
          `${backendUrl}/api/quarterly-reports`,
          formData,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
      }

      if (response.data.success) {
        await fetchReports();
        closeModal();
      }
    } catch (err) {
      console.error('Error saving report:', err);
      setError(err.response?.data?.error || 'Failed to save report');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.delete(
        `${backendUrl}/api/quarterly-reports/${deleteReportId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        await fetchReports();
        setShowDeleteModal(false);
        setDeleteReportId(null);
      }
    } catch (err) {
      console.error('Error deleting report:', err);
      setError(err.response?.data?.error || 'Failed to delete report');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (reportId) => {
    setDeleteReportId(reportId);
    setShowDeleteModal(true);
  };

  const getQuarterColor = (quarter) => {
    switch(quarter) {
      case 'Q1': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Q2': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Q3': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Q4': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getQuarterIcon = (quarter) => {
    switch(quarter) {
      case 'Q1': return <TrendingUp className="h-4 w-4" />;
      case 'Q2': return <BarChart3 className="h-4 w-4" />;
      case 'Q3': return <PieChart className="h-4 w-4" />;
      case 'Q4': return <Award className="h-4 w-4" />;
      default: return <Calendar className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-6 py-8" style={{marginTop:'100px'}}>
        {/* Header Section */}
        <div className="mb-12">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-3">
                Quarterly Reports
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl">
                View and manage comprehensive quarterly business reports with detailed insights and analytics
              </p>
            </div>

            {role === 'owner' && (
              <button
                onClick={() => openModal()}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Plus size={20} className="mr-2" />
                Add New Report
              </button>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
            <div className="flex items-center">
              <X className="h-5 w-5 mr-2" />
              {error}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && !showModal && (
          <div className="flex justify-center items-center py-20">
            <div className="flex flex-col items-center">
              <div className="animate-spin h-12 w-12 border-4 border-blue-500 rounded-full border-t-transparent mb-4"></div>
              <p className="text-gray-600">Loading reports...</p>
            </div>
          </div>
        )}

        {/* Reports Grid */}
      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reports.length === 0 && !loading ? (
            <div className="col-span-full">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-16 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FileText className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Reports Yet</h3>
                <p className="text-gray-500 text-lg mb-8">Get started by creating your first quarterly report.</p>
                {role === 'owner' && (
                  <button
                    onClick={() => openModal()}
                    className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
                  >
                    <Plus size={18} className="mr-2" />
                    Create First Report
                  </button>
                )}
              </div>
            </div>
          ) : (
            reports.map((report) => (
              <div key={report.id} className="group h-full">
                <div className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-blue-200 overflow-hidden transform hover:-translate-y-1 h-full flex flex-col">
                  {/* Report Image */}
                  <div className="relative h-48 bg-gradient-to-br from-blue-500 to-indigo-600 overflow-hidden flex-shrink-0">
                    {report.image_url ? (
                      <img
                        src={report.image_url}
                        alt={report.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText className="h-16 w-16 text-white/70" />
                      </div>
                    )}
                    
                    {/* Quarter Badge */}
                    {report.quarter && (
                      <div className="absolute top-4 left-4">
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getQuarterColor(report.quarter)}`}>
                          {getQuarterIcon(report.quarter)}
                          <span className="ml-1">{report.quarter}</span>
                          {report.year && <span className="ml-1">({report.year})</span>}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons Overlay */}
                    <div className="absolute top-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <a
                        href={report.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-white/90 backdrop-blur-sm text-blue-600 rounded-lg hover:bg-white transition-colors shadow-lg"
                        title="View Report"
                      >
                        <Eye size={16} />
                      </a>
                      
                      {role === 'owner' && (
                        <>
                          <button
                            onClick={() => openModal(report)}
                            className="p-2 bg-white/90 backdrop-blur-sm text-green-600 rounded-lg hover:bg-white transition-colors shadow-lg"
                            title="Edit Report"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => openDeleteModal(report.id)}
                            className="p-2 bg-white/90 backdrop-blur-sm text-red-600 rounded-lg hover:bg-white transition-colors shadow-lg"
                            title="Delete Report"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Report Content */}
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 leading-tight flex-grow">
                      {report.name}
                    </h3>

                    {/* Action Button - Always at bottom */}
                    <div className="pt-4 mt-auto">
                      <a
                        href={report.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center w-full justify-center px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 group"
                      >
                        <ExternalLink size={18} className="mr-2 group-hover:translate-x-1 transition-transform" />
                        View Report
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {editingReport ? 'Edit Report' : 'Create New Report'}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Report Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Report Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="e.g., Q1 2024 Financial Report"
                  />
                </div>

                {/* Report URL or Upload */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Report Link *
                  </label>
                  
                  {/* URL Input */}
                  <div className="space-y-3">
                    <input
                      type="url"
                      name="url"
                      value={formData.url}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="https://example.com/report.pdf"
                    />
                    
                    <div className="text-center text-gray-500 text-sm">
                      OR
                    </div>
                    
                    {/* PDF Upload */}
                    <div className="relative">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handlePdfUpload}
                        className="hidden"
                        id="pdf-upload"
                        disabled={isUploading}
                      />
                      <label
                        htmlFor="pdf-upload"
                        className={`flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-all ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {isUploading ? (
                          <div className="flex items-center">
                            <div className="animate-spin h-5 w-5 border-4 border-blue-500 rounded-full border-t-transparent mr-2"></div>
                            <span className="text-sm text-gray-600">Uploading PDF...</span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <Upload className="h-5 w-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-600">Upload PDF Report</span>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Report Cover Image
                  </label>
                  <div className="space-y-3">
                    {formData.image_url && (
                      <div className="relative">
                        <img
                          src={formData.image_url}
                          alt="Preview"
                          className="w-full h-32 object-cover rounded-xl border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="image-upload"
                        disabled={isUploading}
                      />
                      <label
                        htmlFor="image-upload"
                        className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-all ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {isUploading ? (
                          <div className="flex flex-col items-center">
                            <div className="animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent mb-2"></div>
                            <span className="text-sm text-gray-600">Uploading...</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <ImageIcon className="h-8 w-8 text-gray-400 mb-2" />
                            <span className="text-sm text-gray-600">Click to upload image</span>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                </div>

                {/* Quarter and Year */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Quarter
                    </label>
                    <select
                      name="quarter"
                      value={formData.quarter}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="">Select Quarter</option>
                      <option value="Q1">Q1</option>
                      <option value="Q2">Q2</option>
                      <option value="Q3">Q3</option>
                      <option value="Q4">Q4</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Year
                    </label>
                    <input
                      type="number"
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      min="2020"
                      max="2030"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || isUploading}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} className="mr-2" />
                        {editingReport ? 'Update Report' : 'Create Report'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Delete Report</h2>
                </div>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete this quarterly report? This action cannot be undone and will permanently remove the report from your records.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={loading}
                    className="px-6 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent mr-2"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 size={16} className="mr-2" />
                        Delete Report
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuaReports;